import { useMemo } from 'react';
import { useMsal } from "@azure/msal-react";
import { SortColumn } from '@bst/react-data-grid';
import { DataGridPreferences } from "@bst/react-ui";
import { useDebouncedCallback } from "use-debounce";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useBSTInsightsAPI from "../../../apiclients/api/bstinsightsapi";
import { UserPreferencesDTO, UserDTO } from "../../../apiclients/bstinsights";
import { ComponentsEnum } from "../../../utils/components";
import useAcquireToken from "../../AuthToken/Hooks/useAcquireToken";

export const userManagementGridPreferencesQueryKey =
  "userManagementGridPreferencesQueryKey";

type PreferencePayload = {
  columnSelection?: string[];
} & DataGridPreferences<UserDTO>;

export const useGridPreferences = () => {
  const { getTokenSilently } = useAcquireToken();
  const { getUserPreferences, upsertUserPreferences } = useBSTInsightsAPI();
  const { instance } = useMsal();
  const queryClient = useQueryClient();
  const activeAccount = instance.getActiveAccount();

  const { data: preferences, isFetched } = useQuery({
    queryKey: [userManagementGridPreferencesQueryKey],
    queryFn: async () => {
      const token = await getTokenSilently();
      const userId = activeAccount?.localAccountId || "";
      const preferences: UserPreferencesDTO | undefined =
        await getUserPreferences(
          token,
          userId,
          ComponentsEnum.UserManagementGrid
        );
      return JSON.parse(
        preferences?.settings || '{"columns":{}}'
      ) as PreferencePayload;
    },
    meta: { persist: true },
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const { mutate: updateGridPreferences } = useMutation({
    mutationFn: async (preferences: PreferencePayload) => {
      const token: string = await getTokenSilently();
      const userId = activeAccount?.localAccountId || "";

      const request: UserPreferencesDTO = {
        azureIdentityId: userId,
        componentName: ComponentsEnum.UserManagementGrid,
        settings: JSON.stringify(preferences),
      };

      if (token) {
        await upsertUserPreferences(token, request);
      }
    },
    onMutate: async (newValue) => {
      const prevValue = queryClient.getQueryData([
        userManagementGridPreferencesQueryKey,
      ]);
      queryClient.setQueryData(
        [userManagementGridPreferencesQueryKey],
        newValue
      );
      return { prevValue };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(
        [userManagementGridPreferencesQueryKey],
        context?.prevValue
      );
    },
  });

  const debouncedSaveGridPreferences = useDebouncedCallback(
    (data: PreferencePayload) => {
      updateGridPreferences(data);
    },
    500
  );

  const sort = useMemo<SortColumn | undefined>(() => {
    if (preferences?.sort) {
      return {
        columnKey: preferences.sort.column,
        direction: preferences.sort.direction,
      };
    }
  }, [preferences]);

  return {
    sort,
    isFetched,
    preferences,
    updateGridPreferences,
    debouncedSaveGridPreferences,
  };
};
